import { useEffect, useState } from "react";

import { usePointerDrag } from "./usePointerDrag";

// Keep at least the header on screen so the panel can always be grabbed back.
const MIN_VISIBLE_HEIGHT = 48;
const INITIAL_TOP = 16;

type Position = { left: number; top: number };

const clampPosition = ({ left, top }: Position, width: number): Position => {
  const maxLeft = Math.max(0, window.innerWidth - width);
  const maxTop = Math.max(0, window.innerHeight - MIN_VISIBLE_HEIGHT);
  return {
    left: Math.min(maxLeft, Math.max(0, left)),
    top: Math.min(maxTop, Math.max(0, top)),
  };
};

/**
 * Viewport position for a floating panel dragged by its header. Pointer-based
 * like ResizablePanels (no drag library); the panel is clamped to the viewport
 * on every move and on window resize.
 */
export const useDragPosition = (width: number) => {
  const [position, setPosition] = useState<Position>(() =>
    clampPosition(
      { left: Math.round((window.innerWidth - width) / 2), top: INITIAL_TOP },
      width
    )
  );

  useEffect(() => {
    const handleResize = () =>
      setPosition((current) => clampPosition(current, width));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [width]);

  const followPointer = usePointerDrag();

  const handlePointerDown = (e: React.PointerEvent) => {
    // Header buttons sit inside the drag handle — let them click.
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const start = position;

    followPointer((ev) => {
      setPosition(
        clampPosition(
          {
            left: start.left + ev.clientX - startX,
            top: start.top + ev.clientY - startY,
          },
          width
        )
      );
    });
  };

  return { position, handlePointerDown };
};
